/* Header drawer.
 *
 * Three pieces of state, deliberately kept separate:
 *   open   - is the drawer showing at all
 *   tab    - which of the two fixed tabs (Skincare / Clinics) is active
 *   panel  - which panel within that tab is on screen
 *
 * Panels are stacked in the same box and swapped with the `hidden` attribute
 * rather than transformed, so the drawer keeps one scroll container. Closing
 * or switching tab always returns to that tab's root panel: reopening the menu
 * should never drop you three levels deep in a list you left days ago.
 */
(function () {
  'use strict';

  /* Page scroll lock, shared with the other overlays (assets/scroll-lock.js). */
  function lockPage(on) {
    if (window.DDJScrollLock) window.DDJScrollLock.set('menu', on);
    else document.body.style.overflow = on ? 'hidden' : '';
  }


  var header = document.querySelector('[data-header]');
  if (!header) return;

  var drawer = header.querySelector('[data-header-drawer]');
  var toggle = header.querySelector('[data-drawer-toggle]');
  var backdrop = document.querySelector('[data-drawer-backdrop]');
  if (!drawer || !toggle) return;

  var labelClosed = toggle.querySelector('[data-drawer-label-closed]');
  var labelOpen = toggle.querySelector('[data-drawer-label-open]');
  var tabButtons = Array.prototype.slice.call(header.querySelectorAll('[data-tab]'));
  var tabPanels = Array.prototype.slice.call(drawer.querySelectorAll('[data-tab-panel]'));

  var isOpen = false;
  var activeTab = '1';

  /* The drawer hangs off the bottom of the header and fills what is left of the
   * viewport. That height depends on the header's rendered height, which changes
   * with the announcement bar and the wordmark, so measure rather than guess. */
  function measure() {
    var rect = header.getBoundingClientRect();
    var offset = Math.max(0, rect.bottom);
    header.style.setProperty('--header-height', offset + 'px');
    /* Also publish it on :root, for anything outside the header that needs to
     * clear it. (Full-screen section heights use --chrome-height instead: that
     * is the in-flow chrome, which is a different number when this overlays.) */
    document.documentElement.style.setProperty('--header-height', offset + 'px');
  }

  function panelsIn(tab) {
    var panel = drawer.querySelector('[data-tab-panel="' + tab + '"]');
    if (!panel) return [];
    return Array.prototype.slice.call(panel.querySelectorAll('[data-nav-panel]'));
  }

  function showPanel(tab, id) {
    panelsIn(tab).forEach(function (p) {
      var match = p.getAttribute('data-nav-panel') === id;
      p.hidden = !match;
    });
    // Any row that opens a panel reports whether its panel is the visible one.
    drawer.querySelectorAll('[data-nav-open]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', btn.getAttribute('data-nav-open') === id ? 'true' : 'false');
    });
  }

  function resetPanels(tab) {
    showPanel(tab, 'root-' + tab);
  }

  /* The tab strip is visible in the closed state, so the filled "selected" look
   * has to track whether the drawer is actually open, not just which tab is
   * remembered. Painting the remembered tab while the drawer is shut left
   * SKINCARE filled black over a hidden panel. */
  function paintTabs() {
    tabButtons.forEach(function (btn) {
      var selected = isOpen && btn.getAttribute('data-tab') === activeTab;
      btn.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
  }

  function setTab(tab) {
    activeTab = tab;
    paintTabs();
    tabPanels.forEach(function (panel) {
      panel.hidden = panel.getAttribute('data-tab-panel') !== tab;
    });
    resetPanels(tab);
  }

  /* Opening and closing are animated in CSS (`[data-closing]` in input.css).
   * Entry needs no script: an element's animation starts the moment `hidden`
   * comes off. Exit does, because `hidden` would cut it dead, so close marks
   * the drawer as closing and only hides it once the fade has run. */
  var closeTimer = null;

  function finishClose() {
    clearTimeout(closeTimer);
    closeTimer = null;
    drawer.hidden = true;
    drawer.removeAttribute('data-closing');
    if (backdrop) {
      backdrop.hidden = true;
      backdrop.removeAttribute('data-closing');
    }
    /* Only now does the header go back to white-over-hero. Dropped at the
     * START of the close, the drawer's labels inherited white mid-fade and
     * flashed against its own light panel. */
    header.removeAttribute('data-drawer-open');
    resetPanels(activeTab);
  }

  function open(tab) {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
    measure();
    isOpen = true;
    drawer.removeAttribute('data-closing');
    drawer.hidden = false;
    /* Drops the overlay header's white-on-hero treatment while the drawer is
     * up. The drawer renders inside <header>, so without this its own labels
     * inherit white and vanish against its light panel. */
    header.setAttribute('data-drawer-open', 'true');
    if (backdrop) {
      backdrop.removeAttribute('data-closing');
      backdrop.hidden = false;
    }
    toggle.setAttribute('aria-expanded', 'true');
    if (labelClosed) labelClosed.hidden = true;
    if (labelOpen) labelOpen.hidden = false;
    lockPage(true);
    setTab(tab || activeTab);
  }

  function close() {
    isOpen = false;
    toggle.setAttribute('aria-expanded', 'false');
    if (labelClosed) labelClosed.hidden = false;
    if (labelOpen) labelOpen.hidden = true;
    lockPage(false);
    paintTabs();

    var motion = window.matchMedia && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!motion) {
      finishClose();
      return;
    }
    drawer.setAttribute('data-closing', 'true');
    if (backdrop) backdrop.setAttribute('data-closing', 'true');
    /* A timer rather than `animationend`: the event bubbles up from every
     * animated child in the drawer, and a timer cannot be missed. Matches
     * --motion-base in input.css. */
    closeTimer = setTimeout(finishClose, 320);
  }

  toggle.addEventListener('click', function () {
    if (isOpen) close();
    else open(activeTab);
  });

  /* A tab click opens the drawer on that tab when closed, and switches when
   * already open. The tabs are visible in the closed state, so they have to do
   * both jobs. */
  tabButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var tab = btn.getAttribute('data-tab');
      if (!isOpen) open(tab);
      else if (tab === activeTab) close();
      else setTab(tab);
    });
  });

  drawer.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-nav-open]');
    if (opener) {
      showPanel(activeTab, opener.getAttribute('data-nav-open'));
      return;
    }
    var back = event.target.closest('[data-nav-back]');
    if (back) {
      showPanel(activeTab, back.getAttribute('data-nav-back'));
    }
  });

  if (backdrop) backdrop.addEventListener('click', close);

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isOpen) {
      close();
      toggle.focus();
    }
  });

  window.addEventListener('resize', function () {
    if (isOpen) measure();
  });

  /* Theme editor: re-measure and reset when a block is added or reordered. */
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.contains(header)) measure();
  });

  /* ─── Reveal on scroll up ───
   * Tim, 5 Oct: scrolling down, the header goes with the page; scrolling up it
   * slides back in from the top, solid (white ground, black type) even on the
   * pages where it starts transparent over a picture. Back at the top it
   * settles into its own place again, transparent once more on those pages.
   *
   * It stays put until it has scrolled fully out of sight. Only then is it
   * detached (`data-scroll`: fixed to the top of the screen, parked above it),
   * so the switch is never seen. Where it sits in the flow, its section
   * wrapper keeps its height meanwhile, so the page does not jump up. It
   * re-attaches when the page is back where its own place meets the top of the
   * screen. `always` (Sticky header: Always visible) detaches as soon as the
   * page moves and never hides. Before this, `always` was `sticky` inside a
   * wrapper exactly its own height, so it never actually stuck.
   *
   * The slide is a transform on <header>, `none` once shown. The menu drawer
   * inside it is `absolute`, not `fixed`, so the transform cannot trap it (the
   * Noted 2.0 trap), and the menu can only be opened while the header shows. */
  var mode = header.getAttribute('data-sticky') || 'none';
  if (mode !== 'none') {
    var slot = header.parentElement;
    var home = 0;
    var height = 0;
    var inFlow = true;
    var lastY = window.pageYOffset || 0;
    var ticking = false;
    var DELTA = 6;

    var place = function () {
      if (header.hasAttribute('data-scroll')) {
        height = header.offsetHeight;
        if (inFlow) slot.style.height = height + 'px';
        return;
      }
      home = header.getBoundingClientRect().top + (window.pageYOffset || 0);
      height = header.offsetHeight;
      inFlow = window.getComputedStyle(header).position !== 'absolute';
    };

    var setScroll = function (state) {
      var current = header.getAttribute('data-scroll');
      if (state === current) return;
      if (!state) {
        header.removeAttribute('data-scroll');
        slot.style.height = '';
        return;
      }
      if (current) {
        header.setAttribute('data-scroll', state);
        return;
      }
      if (inFlow) slot.style.height = height + 'px';
      header.setAttribute('data-scroll-instant', '');
      header.setAttribute('data-scroll', state);
      void header.offsetWidth;
      header.removeAttribute('data-scroll-instant');
    };

    var update = function () {
      ticking = false;
      var y = window.pageYOffset || 0;
      var dy = y - lastY;
      lastY = y;
      /* Frozen while the menu or any other overlay holds the page. */
      if (isOpen || document.documentElement.classList.contains('is-scroll-locked')) return;
      if (y <= home) {
        setScroll(null);
        return;
      }
      var state = header.getAttribute('data-scroll');
      if (mode === 'always') {
        setScroll('shown');
        return;
      }
      if (!state) {
        if (y > home + height) setScroll('hidden');
        return;
      }
      /* The bounce past the bottom of the page reads as a scroll up. */
      if (y >= document.documentElement.scrollHeight - window.innerHeight - 1) return;
      /* Its own place is back on screen (a slow scroll up never crosses
       * DELTA): show it there rather than leave the gap empty. */
      if (y < home + height || dy < -DELTA) setScroll('shown');
      else if (dy > DELTA) setScroll('hidden');
    };

    /* Tabbing into a hidden header brings it back, so MENU and CLOSE are seen. */
    header.addEventListener('focusin', function () {
      if (header.getAttribute('data-scroll') === 'hidden') setScroll('shown');
    });

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', place);
    window.addEventListener('load', place);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(place);
    place();
    update();
  }

  measure();
  setTab('1');
})();
